import {
  forwardRef,
  Fragment,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
  type RefAttributes
} from 'react'

import {
  AnimatePresence,
  motion,
  useIsPresent,
  type Variants
} from 'motion/react'

import { useReducedMotion } from '../../../hooks/use-reduced-motion'
import { cn } from '../../../utils/cn'
import {
  Button,
  type ButtonProps,
  type ButtonSize
} from '../../atoms/button/button'
import { Chip } from '../../atoms/chip/chip'
import { Skeleton } from '../../atoms/skeleton/skeleton'
import { Text } from '../../atoms/text/text'
import { TextLink } from '../../atoms/text-link/text-link'
import { Checkbox } from '../../molecules/checkbox/checkbox'
import { Input } from '../../molecules/input/input'
import { Pagination } from '../../molecules/pagination/pagination'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../molecules/select/select'
import { Tabs, TabsIndicator, TabsList, TabsTrigger } from '../tabs/tabs'

import { DescendingTextSortIcon } from './descending-text-sort-icon'
import { EditColumnsIcon } from './edit-columns-icon'
import loadErrorIllustration from './load-error-illustration.svg'
import { OmniTableEmptyState } from './omni-table-empty-state'
import { OmniTableIcon } from './omni-table-icon'
import { OMNI_TABLE_PAGE_SIZES } from './omni-table-preferences'
import { PinnedCellContent } from './pinned-cell-content'
import { TableScrollArea } from './table-scroll-area'
import { TableSearchHighlight } from './table-search-highlight'
import { TableTransition } from './table-transition'

export interface OmniTableTab {
  id: string
  label: ReactNode
  /** Optional count displayed alongside the tab label. */
  count?: number
}

export interface OmniTableFilter {
  id: string
  label: string
  /** Number of applied values, shown beside the chip label. */
  selectedCount?: number
  /** Clears this category without opening its popover. */
  onClear?: () => void
  icon?: ReactNode
  /** Applied filter state overrides search-derived selection, including false. */
  active?: boolean
  /** Exact matches select the chip visually, without applying filters or invoking callbacks. */
  searchValues?: readonly string[]
  /** Opens the owning page's filter controls, receiving the current visual state. */
  onClick?: (active: boolean) => void
  /** Wraps the standard chip button in the owning page's filter popover or dialog. */
  renderTrigger?: (
    trigger: ReactElement<React.ComponentPropsWithRef<'button'>>
  ) => ReactNode
}

export interface OmniTableCellContext {
  /** Highlights literal search matches while preserving the surrounding text or link. */
  highlight: (text: string | number) => ReactNode
}

export interface OmniTableColumn<Row> {
  id: string
  header: ReactNode
  /** Plain text is highlighted automatically; composed cells use context.highlight. */
  cell: (row: Row, context: OmniTableCellContext) => ReactNode
  /** Overrides this column's default slot width for the consuming table. Gutters are outside the slot. Pinned actions use it as their initial fallback. */
  width?: number
  /** Fixed sizing by content; the first name slot also includes its selection checkbox. */
  contentType?:
    | 'name'
    | 'badge'
    | 'number'
    | 'date'
    | 'payment-preference'
    | 'phone'
  align?: 'left' | 'center' | 'right'
  /**
   * Quantitative values align right; qualitative values, including numeric
   * identifiers, align left in headers and cells. Omit valueType to use an
   * explicit align.
   */
  valueType?: 'quantitative' | 'qualitative'
  /** Pins the action group; only the data columns scroll, with 16px action padding. */
  pinned?: 'right'
  sortable?: boolean
  sortIcon?: 'alpha' | 'vertical'
  /**
   * What clicking the header does: `desc-asc` toggles descending and ascending;
   * `asc-desc-none` goes ascending, descending, then back to unsorted. Defaults
   * to `desc-asc` for `sortIcon: 'alpha'` and `asc-desc-none` otherwise.
   */
  sortCycle?: 'asc-desc-none' | 'desc-asc'
  className?: string
}

export interface OmniTableSort {
  columnId: string
  direction: 'asc' | 'desc'
}

export interface OmniTableSelectionChange {
  /** A row checkbox, the checkbox that toggles every selectable row on the page, or Clear selection when the selection has no `onClear`. */
  kind: 'row' | 'page' | 'clear'
  checked: boolean
  /** The toggled row, for `kind: 'row'`. */
  rowId?: string
}

export interface OmniTableSelection {
  selectedIds: readonly string[]
  onChange: (ids: string[], change: OmniTableSelectionChange) => void
  /** Size of the bar's standard buttons and matching corner radius. Defaults to md. */
  size?: ButtonSize
  /** Rows that return false have a disabled checkbox and are left out of page selection. */
  isRowSelectable?: (rowId: string) => boolean
  /** Count may include selected rows outside the current page. */
  totalSelected?: number
  /** All matching results, including other pages, are selected. */
  allSelected?: boolean
  onSelectAll?: () => void
  onClear?: () => void
  /** Consumer-owned actions for the current context, rendered after the standard selection controls. */
  actions?: ReactNode
}

export type OmniTablePageChangeMethod = 'previous' | 'next' | 'number' | 'jump'

export interface OmniTablePagination {
  page: number
  totalPages: number
  onPageChange: (page: number, method: OmniTablePageChangeMethod) => void
  /**
   * Called instead of moving to the nearest page when the Page field holds a
   * page outside 1 to totalPages, or not a number. Without it, an out-of-range
   * page moves to the nearest one.
   */
  onInvalidPage?: () => void
  /** All matching rows across pages; the footer then shows the current page's range, such as "11-20 of 113". */
  totalItems?: number
  pageSize: number
  onPageSizeChange: (size: number) => void
  disabled?: boolean
}

export interface OmniTableProps<Row>
  extends Omit<React.ComponentPropsWithRef<'div'>, 'children'> {
  tabs?: readonly OmniTableTab[]
  activeTab?: string
  onTabChange?: (id: string) => void
  /** Focuses the active tab on mount, for pages that mount a new table per tab. */
  focusActiveTab?: boolean
  /**
   * For pages that mount a new table per tab: the tab shown before this one,
   * so the rows slide in the way the tabs moved, as when one table changes tabs.
   * Tables with the same tabs share the indicator, which moves over by itself.
   */
  previousTab?: string
  /** Omit `onSearchChange` for a table with no search; nothing is highlighted then. */
  searchValue?: string
  onSearchChange?: (value: string) => void
  searchPlaceholder?: string
  /** Consumer controls beside the search field, before Edit columns. */
  toolbarActions?: ReactNode
  /** Consumer content below the search row, such as applied filters. */
  toolbarFooter?: ReactNode
  onEditColumns?: () => void
  /** Wraps the standard button in the consumer's column organizer or popover. */
  renderEditColumns?: (trigger: ReactElement<ButtonProps>) => ReactNode
  quickFilters?: readonly OmniTableFilter[]
  /** Clears every applied quick-filter category, preserving the search value. */
  onClearFilters?: () => void
  columns: readonly OmniTableColumn<Row>[]
  /** Rows for the current page; the consumer owns filtering and pagination. */
  rows: readonly Row[]
  getRowId: (row: Row) => string
  /** Consumer-owned row interactions and accessible attributes. */
  getRowProps?: (row: Row) => React.ComponentPropsWithoutRef<'tr'>
  /**
   * Rows shown under a row, in the same columns; a union `Row` type tells
   * them apart in each cell. They stay mounted and open or close with
   * `isRowExpanded`. The consumer renders the toggle, with `aria-expanded`
   * and `aria-controls` naming the ids `getRowProps` gives these rows.
   */
  getSubRows?: (row: Row) => readonly Row[] | undefined
  isRowExpanded?: (row: Row) => boolean
  /** Replaces the checkbox gutter with a row control when selection is absent. */
  renderRowControl?: (row: Row) => ReactNode
  selection?: OmniTableSelection
  /** Selection, pagination and tab motion; reduced-motion preferences take precedence. */
  animated?: boolean
  sort?: OmniTableSort
  onSortChange?: (sort: OmniTableSort | undefined) => void
  pagination: OmniTablePagination
  /** Initial grid height until the available space in the card is measured. */
  viewportHeight?: number
  /** Card height in pixels, or its maximum height when fitContent is enabled. */
  cardHeight?: number
  /** Shrinks the card to its content, retaining cardHeight as its maximum. */
  fitContent?: boolean
  /**
   * Fits the card to its rows, up to this many pixels above the window's
   * bottom edge; only a window too short for the controls, the header and five
   * rows (or all of them, when fewer) lets the card pass that edge. Omit it to
   * keep the card at 597px.
   */
  viewportBottomGap?: number
  minTableWidth?: number
  /** Space between data slots in pixels. Defaults to 48; outer insets remain 16px. */
  columnGap?: number
  loading?: boolean
  error?: ReactNode
  /** Replaces the grid and pagination when no rows match; controls remain visible. */
  emptyMessage?: ReactNode
  /** Accessible name for the native table. */
  tableLabel: string
}

const SELECTION_REVEAL_SPRING = {
  type: 'spring',
  duration: 0.22,
  bounce: 0.12
} as const
const SELECTION_COLUMN_WIDTH = 48
const SELECTION_BAR_VARIANTS: Variants = {
  hidden: {
    opacity: 0,
    y: 0,
    scale: 0.97,
    transition: {
      duration: 0.1,
      ease: 'easeIn',
      staggerChildren: 0.01,
      staggerDirection: -1
    }
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      ...SELECTION_REVEAL_SPRING,
      opacity: { duration: 0.1 },
      staggerChildren: 0.01
    }
  }
}
const SELECTION_ITEM_VARIANTS: Variants = {
  hidden: { opacity: 0, y: 6, transition: { duration: 0.08 } },
  visible: { opacity: 1, y: 0, transition: SELECTION_REVEAL_SPRING }
}

const columnStyle = (column: { width?: number }): CSSProperties => ({
  minWidth: column.width,
  width: column.width
})

function hasAppliedFilterValues(filter: OmniTableFilter) {
  return filter.selectedCount === undefined
    ? filter.active === true
    : filter.selectedCount > 0
}

const COLUMN_WIDTHS = {
  name: 264,
  badge: 104,
  number: 128,
  date: 128,
  'payment-preference': 186,
  phone: 136
} satisfies Record<NonNullable<OmniTableColumn<unknown>['contentType']>, number>
const DEFAULT_COLUMN_GAP = 48
const CELL_EDGE_PADDING = 16

const cellAlign = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right'
} as const

const columnAlign = (
  column: Pick<OmniTableColumn<unknown>, 'align' | 'valueType' | 'contentType'>
) =>
  cellAlign[
    column.contentType === 'number' || column.valueType === 'quantitative'
      ? 'right'
      : column.valueType === 'qualitative'
      ? 'left'
      : column.align ?? 'left'
  ]

function TableCellContent({
  content,
  context
}: {
  content: ReactNode
  context: OmniTableCellContext
}) {
  return typeof content === 'string' || typeof content === 'number' ? (
    <Text as='span' variant='body-lg' className='text-inherit'>
      {context.highlight(content)}
    </Text>
  ) : (
    content
  )
}

function PageJump({ pagination }: { pagination: OmniTablePagination }) {
  const { page, totalPages, disabled, onPageChange, onInvalidPage } = pagination
  const [draft, setDraft] = useState(String(page))
  const [pending, setPending] = useState(false)

  useEffect(() => {
    setDraft(String(page))
    setPending(false)
  }, [page, totalPages, disabled])

  const commit = useCallback(() => {
    if (!draft.trim()) {
      setPending(false)
      setDraft(String(page))
      return
    }
    if (!pending || disabled) return
    setPending(false)
    const value = Number(draft)
    if (!Number.isSafeInteger(value) || !totalPages) {
      onInvalidPage?.()
      setDraft(String(page))
      return
    }
    if (onInvalidPage && (value < 1 || value > totalPages)) {
      onInvalidPage()
      setDraft(String(page))
      return
    }
    const next = Math.min(Math.max(value, 1), totalPages)
    setDraft(String(next))
    if (next !== page) onPageChange(next, 'jump')
  }, [draft, pending, disabled, page, totalPages, onInvalidPage, onPageChange])

  useEffect(() => {
    if (!pending || !draft.trim() || disabled) return
    const timer = setTimeout(commit, 300)
    return () => clearTimeout(timer)
  }, [pending, draft, disabled, commit])

  return (
    <label className='text-surface-body text-body-lg flex shrink-0 items-center gap-2'>
      <Text as='span' variant='body-lg' className='text-inherit'>
        Page
      </Text>
      <input
        aria-label='Go to page'
        inputMode='numeric'
        pattern='[0-9]*'
        className='border-button-secondary-border bg-surface text-surface-body text-body-lg focus-visible:border-primary focus-visible:outline-primary box-content min-w-6 rounded-[4px] border px-1 py-0.5 text-center'
        style={{
          width: `${Math.max(1, String(totalPages).length)}ch`
        }}
        value={draft}
        disabled={disabled || totalPages < 1}
        onFocus={() => setDraft('')}
        onChange={event => {
          setDraft(event.target.value)
          setPending(true)
        }}
        onBlur={commit}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            event.preventDefault()
            event.currentTarget.blur()
          }
        }}
      />
      <Text as='span' variant='body-lg' className='text-inherit'>
        of {totalPages}
      </Text>
    </label>
  )
}

function TableFooter({
  pagination,
  animated
}: {
  pagination: OmniTablePagination
  animated: boolean
}) {
  const pageSizeLabelId = useId()

  return (
    <div
      data-slot='omni-table-footer'
      className='grid min-h-[50px] grid-cols-[1fr_auto_1fr] items-center gap-4 max-lg:grid-cols-1 max-lg:justify-items-center'
    >
      <div className='justify-self-start max-lg:justify-self-center'>
        <PageJump pagination={pagination} />
      </div>
      <Pagination
        animated={animated}
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        onPageChange={pagination.onPageChange}
        disabled={pagination.disabled}
      />
      <div className='text-surface-body text-body-lg flex items-center gap-2 justify-self-end max-lg:justify-self-center'>
        {pagination.totalItems !== undefined && pagination.totalItems > 0 && (
          <Text
            as='span'
            variant='body-lg'
            data-slot='omni-table-range'
            className='mr-4 text-inherit'
          >
            {(pagination.page - 1) * pagination.pageSize + 1}-
            {Math.min(
              pagination.page * pagination.pageSize,
              pagination.totalItems
            )}{' '}
            of {pagination.totalItems}
          </Text>
        )}
        <Text
          as='span'
          variant='body-lg'
          id={pageSizeLabelId}
          className='text-inherit'
        >
          Rows per page
        </Text>
        <Select
          value={String(pagination.pageSize)}
          onValueChange={value => {
            if (value !== null) pagination.onPageSizeChange(Number(value))
          }}
          disabled={pagination.disabled}
        >
          <SelectTrigger
            aria-labelledby={pageSizeLabelId}
            className='border-surface-300 text-surface-body text-caption-lg h-9 min-w-[53px] gap-1 rounded-lg px-2 font-normal *:data-[slot=select-value]:line-clamp-none'
          >
            <SelectValue className='min-w-[2ch] flex-none shrink-0 leading-[18px]'>
              {(value: string | null) => (value === '5' ? '05' : value)}
            </SelectValue>
          </SelectTrigger>
          <SelectContent side='top' align='end' className='min-w-[70px]'>
            {OMNI_TABLE_PAGE_SIZES.map(size => (
              <SelectItem key={size} value={String(size)}>
                {size === 5 ? '05' : size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}

function SelectAllButton({
  onClick,
  size,
  disabled
}: {
  onClick: () => void
  size: ButtonSize
  disabled: boolean
}) {
  const isPresent = useIsPresent()
  return (
    <Button
      size={size}
      variant='secondary'
      disabled={disabled || !isPresent}
      aria-hidden={disabled || !isPresent || undefined}
      onClick={onClick}
    >
      Select all
    </Button>
  )
}

const SELECTION_BAR_RADIUS: Record<ButtonSize, string> = {
  sm: 'rounded-button-sm',
  md: 'rounded-button-md',
  lg: 'rounded-button-lg'
}

function SelectionBarContent({
  selection,
  count,
  animated,
  visible
}: {
  selection: OmniTableSelection
  count: number
  animated: boolean
  visible: boolean
}) {
  const size = selection.size ?? 'md'
  const barRef = useRef<HTMLDivElement>(null)
  const itemVariants = animated ? SELECTION_ITEM_VARIANTS : undefined

  useEffect(() => {
    barRef.current?.toggleAttribute('inert', !visible)
  }, [visible])

  return (
    <motion.div
      ref={barRef}
      data-slot='omni-table-selection-bar'
      data-motion-enabled={animated || undefined}
      role='toolbar'
      aria-label='Selected row actions'
      aria-hidden={!visible || undefined}
      initial={animated ? 'hidden' : false}
      animate={
        animated
          ? visible
            ? 'visible'
            : 'hidden'
          : { opacity: visible ? 1 : 0, scale: 1, transition: { duration: 0 } }
      }
      variants={animated ? SELECTION_BAR_VARIANTS : undefined}
      style={{ transformOrigin: '50% 100%' }}
      className={cn(
        'border-surface-300 bg-surface text-surface-title-50 text-body-md mx-auto mt-2 flex w-fit max-w-full flex-wrap items-center gap-4 border px-6 py-2 shadow-[0_8px_6px_rgba(42,46,81,0.04)]',
        SELECTION_BAR_RADIUS[size]
      )}
    >
      <motion.span variants={itemVariants} className='whitespace-nowrap'>
        <motion.span
          key={selection.allSelected ? 'all' : 'partial'}
          className='inline-block'
          initial={animated ? { opacity: 0, y: 3 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: animated ? 0.08 : 0 }}
        >
          <Text
            as='span'
            variant='body-md'
            weight='semibold'
            className='text-surface-title-50'
          >
            {selection.allSelected ? 'All selected' : `${count} selected`}
          </Text>
        </motion.span>
      </motion.span>
      <motion.div className='flex flex-wrap items-center gap-6'>
        <motion.div
          variants={itemVariants}
          className='relative flex items-center gap-2'
        >
          <AnimatePresence initial={false} mode='popLayout'>
            {selection.onSelectAll && !selection.allSelected && (
              <motion.span
                key='select-all'
                className='inline-flex'
                initial={animated ? { opacity: 0, scale: 0.9 } : false}
                animate={{ opacity: 1, scale: 1 }}
                exit={animated ? { opacity: 0, scale: 0.9 } : undefined}
                transition={{
                  ...SELECTION_REVEAL_SPRING,
                  opacity: { duration: 0.08 }
                }}
              >
                <SelectAllButton
                  onClick={selection.onSelectAll}
                  size={size}
                  disabled={!visible}
                />
              </motion.span>
            )}
          </AnimatePresence>
          <motion.span className='inline-flex'>
            <Button
              size={size}
              variant='secondary'
              disabled={!visible}
              onClick={
                selection.onClear ??
                (() =>
                  selection.onChange([], { kind: 'clear', checked: false }))
              }
            >
              Clear selection
            </Button>
          </motion.span>
        </motion.div>
        {selection.actions && (
          <>
            <motion.span
              variants={itemVariants}
              className='bg-surface-300 h-[34px] w-px'
              aria-hidden='true'
            />
            <motion.div
              variants={itemVariants}
              className='flex flex-wrap items-center gap-2'
            >
              {selection.actions}
            </motion.div>
          </>
        )}
      </motion.div>
    </motion.div>
  )
}

function SelectionBar({
  selection,
  animated,
  count,
  containerRef
}: {
  selection?: OmniTableSelection
  animated: boolean
  count: number
  containerRef: React.Ref<HTMLDivElement>
}) {
  const reducedMotion = useReducedMotion()
  const shouldAnimate = animated && !reducedMotion
  const configured = Boolean(selection)
  const open = configured && count > 0
  const [hasOpened, setHasOpened] = useState(open)
  const [contentHeight, setContentHeight] = useState(0)
  const contentRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (open) setHasOpened(true)
  }, [open])

  useLayoutEffect(() => {
    const content = contentRef.current
    if (!content) return
    const measure = () => setContentHeight(content.offsetHeight)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(content)
    return () => observer.disconnect()
  }, [hasOpened, selection?.size, configured])

  return (
    <div
      ref={containerRef}
      data-slot='omni-table-selection-area'
      className='grid min-w-0 justify-items-center'
    >
      <motion.div
        data-slot='omni-table-selection-reveal'
        data-motion-enabled={shouldAnimate || undefined}
        className='grid w-full justify-items-center'
        initial={false}
        animate={{
          height: open ? contentHeight : 0,
          overflow: 'hidden',
          transitionEnd: { overflow: open ? 'visible' : 'hidden' }
        }}
        transition={{
          height: shouldAnimate
            ? open
              ? SELECTION_REVEAL_SPRING
              : { duration: 0.14, ease: [0.22, 1, 0.36, 1] }
            : { duration: 0 },
          overflow: { duration: 0 }
        }}
      >
        <div
          ref={contentRef}
          data-slot='omni-table-selection-content'
          className='grid w-full justify-items-center'
        >
          {selection && hasOpened && (
            <SelectionBarContent
              selection={selection}
              count={count}
              animated={shouldAnimate}
              visible={open}
            />
          )}
        </div>
      </motion.div>
    </div>
  )
}

const CARD_DEFAULT_HEIGHT = 597
/** With viewportBottomGap, the rows a short window still shows. */
const MIN_VISIBLE_ROWS = 5
/** With viewportBottomGap and no rows to measure, the least card height. */
const CARD_MIN_VIEWPORT_HEIGHT = 240
/** The body's gap-2 between the grid and the footer. */
const GRID_FOOTER_GAP = 8

function OmniTableInner<Row>(
  {
    tabs,
    activeTab,
    onTabChange,
    focusActiveTab = false,
    previousTab,
    searchValue = '',
    onSearchChange,
    searchPlaceholder = 'Search',
    toolbarActions,
    toolbarFooter,
    onEditColumns,
    renderEditColumns,
    quickFilters,
    onClearFilters,
    columns,
    rows,
    getRowId,
    getRowProps,
    getSubRows,
    isRowExpanded,
    renderRowControl,
    selection,
    animated = true,
    sort,
    onSortChange,
    pagination,
    viewportHeight = 350,
    cardHeight: fixedCardHeight = CARD_DEFAULT_HEIGHT,
    fitContent = false,
    viewportBottomGap,
    minTableWidth = 0,
    columnGap = DEFAULT_COLUMN_GAP,
    loading = false,
    error,
    emptyMessage = <OmniTableEmptyState />,
    tableLabel,
    className,
    ...props
  }: OmniTableProps<Row>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const reducedMotion = useReducedMotion()
  const shouldAnimate = animated && !reducedMotion
  const resolvedColumnGap = Math.max(0, columnGap)
  const pinnedLeadingPadding = Math.min(CELL_EDGE_PADDING, resolvedColumnGap)
  const showFeedback = !loading && (!!error || rows.length === 0)
  const cardRef = useRef<HTMLDivElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const footerRef = useRef<HTMLDivElement>(null)
  const selectionAreaRef = useRef<HTMLDivElement>(null)
  const selectedCount = selection
    ? Math.max(selection.totalSelected ?? 0, selection.selectedIds.length)
    : 0
  const hasSelection = selectedCount > 0
  const hasLeadingColumn = Boolean(selection || renderRowControl)
  const [selectionAreaHeight, setSelectionAreaHeight] = useState(0)
  const reserveSelectionSpace = hasSelection || selectionAreaHeight > 0
  const [viewportAvailableHeight, setViewportAvailableHeight] =
    useState<number>()
  useLayoutEffect(() => {
    const area = selectionAreaRef.current
    if (!area) return
    // Measuring the animated reveal keeps the table and bar on one height spring.
    const measure = () => setSelectionAreaHeight(area.offsetHeight)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(area)
    return () => observer.disconnect()
  }, [hasSelection, selection?.size])
  const [viewportCardHeight, setViewportCardHeight] =
    useState(CARD_DEFAULT_HEIGHT)
  const baseCardHeight =
    viewportBottomGap === undefined ? fixedCardHeight : viewportCardHeight
  const heightBudget =
    viewportBottomGap === undefined
      ? Math.min(baseCardHeight, viewportAvailableHeight ?? baseCardHeight)
      : viewportAvailableHeight ?? baseCardHeight
  const cardHeight =
    selectionAreaHeight > 0
      ? Math.max(
          0,
          Math.min(baseCardHeight, heightBudget - selectionAreaHeight)
        )
      : baseCardHeight
  useLayoutEffect(() => {
    const card = cardRef.current
    if (!card || (viewportBottomGap === undefined && !reserveSelectionSpace))
      return
    const area = card.querySelector<HTMLElement>(
      '[data-slot="omni-table-scroll-area"]'
    )
    const grid = card.querySelector<HTMLElement>(
      '[data-slot="omni-table-viewport"]'
    )
    const gridRows = grid?.firstElementChild as HTMLElement | null | undefined
    const measure = () => {
      // offsetTop ignores scrolling, so the result is the same at any scroll position.
      let top = 0
      for (
        let node: HTMLElement | null = card;
        node;
        node = node.offsetParent as HTMLElement | null
      ) {
        top += node.offsetTop
      }
      const available = Math.max(
        0,
        window.innerHeight - top - (viewportBottomGap ?? 0)
      )
      setViewportAvailableHeight(available)
      if (viewportBottomGap === undefined) return
      // While loading, the rows are a placeholder: keep the height they
      // replaced, within the window.
      if (loading) {
        setViewportCardHeight(height =>
          Math.min(height, Math.max(CARD_MIN_VIEWPORT_HEIGHT, available))
        )
        return
      }
      // The card fits its rows at their height plus everything else it holds:
      // the controls (card minus body), the footer and its gap, and the grid's
      // frame (area minus grid). None of these depends on the card's current
      // height; the grid's own height does, a render late, and would oscillate.
      const body = bodyRef.current
      const footer = footerRef.current
      // Rows with no height are not laid out yet (a hidden parent): use 597px.
      if (!body || !footer || !area || !grid || !gridRows?.offsetHeight) {
        setViewportCardHeight(
          Math.min(
            CARD_DEFAULT_HEIGHT,
            Math.max(CARD_MIN_VIEWPORT_HEIGHT, available)
          )
        )
        return
      }
      const frame =
        card.offsetHeight -
        body.clientHeight +
        footer.offsetHeight +
        GRID_FOOTER_GAP +
        area.offsetHeight -
        grid.clientHeight
      // A short window still shows the header and five rows; closed sub-rows
      // have no height and do not count.
      const least =
        frame +
        (gridRows.querySelector<HTMLElement>('thead')?.offsetHeight ?? 0) +
        Array.from(gridRows.querySelectorAll<HTMLElement>('tbody tr'))
          .map(row => row.offsetHeight)
          .filter(Boolean)
          .slice(0, MIN_VISIBLE_ROWS)
          .reduce((height, row) => height + row, 0)
      setViewportCardHeight(
        Math.min(frame + gridRows.offsetHeight, Math.max(least, available))
      )
    }
    measure()
    window.addEventListener('resize', measure)
    // Content above the card, such as a dismissed banner, moves it without a
    // window resize; the rows change with each page, tab and loading state.
    const observer =
      typeof ResizeObserver === 'undefined'
        ? undefined
        : new ResizeObserver(measure)
    for (let node = card.parentElement; node; node = node.parentElement) {
      observer?.observe(node)
    }
    if (gridRows) observer?.observe(gridRows)
    return () => {
      window.removeEventListener('resize', measure)
      observer?.disconnect()
    }
  }, [viewportBottomGap, showFeedback, loading, reserveSelectionSpace])
  // The empty view never runs past the card: the card is at least its
  // controls plus the empty content, whatever height it was given.
  const emptyContentRef = useRef<HTMLDivElement>(null)
  const [emptyMinHeight, setEmptyMinHeight] = useState<number>()
  const feedbackMinHeight =
    selectionAreaHeight > 0
      ? Math.min(emptyMinHeight ?? cardHeight, cardHeight)
      : emptyMinHeight
  useLayoutEffect(() => {
    const card = cardRef.current
    const body = bodyRef.current
    const content = emptyContentRef.current
    if (!showFeedback || !card || !body || !content) return
    const measure = () => {
      const contentHeight = Array.from(content.children).reduce(
        (height, child) => height + (child as HTMLElement).offsetHeight,
        0
      )
      setEmptyMinHeight(card.offsetHeight - body.clientHeight + contentHeight)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(card)
    Array.from(content.children).forEach(child => observer.observe(child))
    return () => observer.disconnect()
  }, [showFeedback, emptyMessage, error])
  const [scrollAreaHeight, setScrollAreaHeight] = useState(viewportHeight)
  useLayoutEffect(() => {
    if (showFeedback) return
    const body = bodyRef.current
    const footer = footerRef.current
    const card = cardRef.current
    if (!body || !footer || !card) return
    const measure = () => {
      if (body.clientHeight > 0 && footer.offsetHeight > 0) {
        // Use the maximum card height so a short page can grow again on navigation.
        const bodyHeight = fitContent
          ? cardHeight - (card.offsetHeight - body.clientHeight)
          : body.clientHeight
        setScrollAreaHeight(
          Math.max(0, bodyHeight - footer.offsetHeight - GRID_FOOTER_GAP)
        )
      }
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(card)
    observer.observe(body)
    observer.observe(footer)
    return () => observer.disconnect()
  }, [showFeedback, activeTab, viewportHeight, fitContent, cardHeight])
  const tabsListRef = useRef<HTMLDivElement>(null)
  const activeTabIndex = tabs?.findIndex(tab => tab.id === activeTab) ?? 0
  const previousTabIndex =
    previousTab === undefined
      ? -1
      : tabs?.findIndex(tab => tab.id === previousTab) ?? -1
  // Read on mount only: 1 when the table arrives from a tab to its left.
  const [enterDirection] = useState(() =>
    shouldAnimate && previousTabIndex >= 0
      ? Math.sign(activeTabIndex - previousTabIndex)
      : 0
  )
  // One indicator for every table with these tabs: when a page swaps the
  // table for another tab, the new indicator moves over from the old one.
  const indicatorLayoutId =
    tabs && shouldAnimate
      ? `omni-table-tab-indicator:${tabs.map(tab => tab.id).join('|')}`
      : undefined
  useEffect(() => {
    if (!focusActiveTab) return
    tabsListRef.current
      ?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')
      ?.focus()
    // Only on mount: the page asks for focus when it mounts this table for a new tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const columnOrder = columns.map(column => column.id).join('|')
  const normalizedSearch = searchValue.trim().toLowerCase()
  const appliedQuickFilterCount =
    quickFilters?.filter(hasAppliedFilterValues).length ?? 0
  const cellContext = useMemo<OmniTableCellContext>(() => {
    const query = searchValue.trim()
    const matcher = query
      ? new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
      : undefined
    return {
      highlight: text => (
        <TableSearchHighlight text={String(text)} matcher={matcher} />
      )
    }
  }, [searchValue])
  const viewKey = JSON.stringify([
    activeTab,
    pagination.page,
    pagination.pageSize
  ])
  const pageIds = rows.map(getRowId)
  const widthScope = JSON.stringify([viewKey, columnOrder, pageIds])
  const [pinnedWidths, setPinnedWidths] = useState<{
    scope: string
    widths: Record<string, Record<string, number>>
  }>({ scope: '', widths: {} })
  const measurePinnedWidth = useCallback(
    (columnId: string, rowId: string, width: number) => {
      setPinnedWidths(current => {
        const widths = current.scope === widthScope ? current.widths : {}
        if (widths[columnId]?.[rowId] === width) return current
        return {
          scope: widthScope,
          widths: {
            ...widths,
            [columnId]: { ...widths[columnId], [rowId]: width }
          }
        }
      })
    },
    [widthScope]
  )
  const firstDataColumn = columns.find(column => column.pinned !== 'right')
  const firstPinnedColumn = columns.find(column => column.pinned === 'right')
  const getColumnPadding = (column: OmniTableColumn<Row>) => {
    const nextColumn = columns[columns.indexOf(column) + 1]
    return {
      paddingLeft:
        column.pinned === 'right'
          ? pinnedLeadingPadding
          : !hasLeadingColumn && column === firstDataColumn
          ? CELL_EDGE_PADDING
          : 0,
      paddingRight:
        !nextColumn || column.pinned === 'right'
          ? CELL_EDGE_PADDING
          : nextColumn.pinned === 'right'
          ? resolvedColumnGap - pinnedLeadingPadding
          : resolvedColumnGap
    }
  }
  const getColumnWidth = (column: OmniTableColumn<Row>) => {
    const widths =
      pinnedWidths.scope === widthScope && pinnedWidths.widths[column.id]
    if (column.pinned === 'right') {
      return widths
        ? Math.max(...Object.values(widths)) +
            pinnedLeadingPadding +
            CELL_EDGE_PADDING
        : column.width ?? 64
    }
    const selectionWidth =
      hasLeadingColumn &&
      column === firstDataColumn &&
      column.contentType === 'name'
        ? SELECTION_COLUMN_WIDTH
        : 0
    const width =
      column.width ??
      (column.contentType
        ? COLUMN_WIDTHS[column.contentType]
        : COLUMN_WIDTHS.number)
    const { paddingLeft, paddingRight } = getColumnPadding(column)
    return Math.max(0, width - selectionWidth) + paddingLeft + paddingRight
  }
  const isSelectable = (id: string) => selection?.isRowSelectable?.(id) ?? true
  const selectablePageIds = pageIds.filter(isSelectable)
  const selectedOnPage = selection
    ? selectablePageIds.filter(id => selection.selectedIds.includes(id))
    : []
  const allOnPage =
    selectablePageIds.length > 0 &&
    selectedOnPage.length === selectablePageIds.length
  // Keep at least one other data column visible beside the frozen first column,
  // the selection checkboxes and the pinned actions.
  const minimumPinnedViewportWidth = firstDataColumn
    ? (hasLeadingColumn ? SELECTION_COLUMN_WIDTH : 0) +
      (getColumnWidth(firstDataColumn) ?? Infinity) +
      columns.reduce(
        (total, column) =>
          total + (column.pinned === 'right' ? getColumnWidth(column) ?? 0 : 0),
        0
      ) +
      Math.min(
        ...columns
          .filter(
            column => column !== firstDataColumn && column.pinned !== 'right'
          )
          .map(column => getColumnWidth(column) ?? Infinity)
      )
    : Infinity

  const getColumnStyle = (
    column: OmniTableColumn<Row>,
    pinFirstColumn: boolean
  ): CSSProperties => ({
    ...columnStyle({ width: getColumnWidth(column) }),
    ...getColumnPadding(column),
    left:
      pinFirstColumn && column === firstDataColumn
        ? hasLeadingColumn
          ? SELECTION_COLUMN_WIDTH
          : 0
        : undefined
  })
  const getTableWidth = () =>
    (hasLeadingColumn ? SELECTION_COLUMN_WIDTH : 0) +
    columns.reduce((total, column) => total + (getColumnWidth(column) ?? 0), 0)

  const togglePage = (checked: boolean) => {
    if (!selection) return
    const next = new Set(selection.selectedIds)
    selectablePageIds.forEach(id => (checked ? next.add(id) : next.delete(id)))
    selection.onChange([...next], { kind: 'page', checked })
  }

  const toggleRow = (id: string, checked: boolean) => {
    if (!selection) return
    const next = new Set(selection.selectedIds)
    if (checked) next.add(id)
    else next.delete(id)
    selection.onChange([...next], { kind: 'row', checked, rowId: id })
  }

  const editColumnsButton = (
    <Button size='lg' variant='secondary' onClick={onEditColumns}>
      <EditColumnsIcon />
      Edit columns
    </Button>
  )

  return (
    <div
      ref={ref}
      data-slot='omni-table'
      className={cn('font-body w-full', className)}
      {...props}
    >
      <div
        ref={cardRef}
        data-slot='omni-table-card'
        className='border-surface-300 bg-surface shadow-dialog flex shrink-0 flex-col rounded-[16px] border pb-2 pl-6 pr-6 pt-6'
        style={{
          height: fitContent && !showFeedback ? undefined : cardHeight,
          maxHeight: fitContent ? cardHeight : undefined,
          minHeight: showFeedback ? feedbackMinHeight : undefined
        }}
      >
        <div className='flex min-h-0 flex-1 flex-col gap-4'>
          {tabs && activeTab && (
            <Tabs value={activeTab} onValueChange={onTabChange}>
              <TabsList
                ref={tabsListRef}
                variant='line'
                className='relative max-w-full justify-start gap-6 overflow-x-auto overflow-y-hidden border-0 [&::-webkit-scrollbar]:hidden'
                style={{ scrollbarWidth: 'none' }}
                render={<motion.div layoutScroll />}
              >
                {tabs.map(tab => (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.id}
                    className='text-body-xl data-active:font-semibold px-0 pb-0 pt-0 font-normal after:hidden'
                  >
                    <Text
                      as='span'
                      variant='body-xl'
                      weight={tab.id === activeTab ? 'semibold' : 'regular'}
                      className='text-inherit'
                    >
                      {tab.label}
                      {tab.count !== undefined && ` (${tab.count})`}
                    </Text>
                  </TabsTrigger>
                ))}
                <TabsIndicator
                  aria-hidden='true'
                  className='bg-primary pointer-events-none absolute bottom-0 h-0.5'
                  style={{
                    left: 'var(--active-tab-left)',
                    width: 'var(--active-tab-width)'
                  }}
                  render={
                    <motion.span
                      layout={shouldAnimate}
                      layoutId={indicatorLayoutId}
                      initial={false}
                      transition={{
                        type: 'spring',
                        stiffness: 500,
                        damping: 40
                      }}
                    />
                  }
                />
              </TabsList>
            </Tabs>
          )}

          {(onSearchChange ||
            toolbarActions ||
            renderEditColumns ||
            onEditColumns) && (
            <div
              data-slot='omni-table-toolbar'
              className={cn('flex min-w-0 items-center gap-2', tabs && 'mt-2')}
            >
              {onSearchChange && (
                <Input
                  size='lg'
                  aria-label='Search table'
                  placeholder={searchPlaceholder}
                  value={searchValue}
                  onChange={event => onSearchChange(event.target.value)}
                  leadingIcon={<OmniTableIcon name='search' size='lg' />}
                  className='[&_[data-slot=input-leading-icon]]:text-surface-title-50 h-12 min-w-0 flex-1 rounded-xl'
                />
              )}
              {toolbarActions}
              {renderEditColumns
                ? renderEditColumns(editColumnsButton)
                : onEditColumns && editColumnsButton}
            </div>
          )}

          {toolbarFooter}

          {quickFilters && quickFilters.length > 0 && (
            <div
              data-slot='omni-table-filters'
              className='flex flex-wrap items-center gap-4'
            >
              <Text
                as='span'
                variant='caption-lg'
                className='text-surface-title-50 whitespace-nowrap'
              >
                Quick filters:
              </Text>
              <div className='flex flex-wrap items-center gap-2'>
                {quickFilters.map(filter => {
                  const canClear = Boolean(
                    filter.onClear && hasAppliedFilterValues(filter)
                  )
                  const active =
                    filter.active ??
                    (hasAppliedFilterValues(filter) ||
                      (normalizedSearch.length > 0 &&
                        !!filter.searchValues?.some(
                          value =>
                            value.trim().toLowerCase() === normalizedSearch
                        )))
                  const trigger = (
                    <button
                      type='button'
                      data-slot='omni-table-filter-trigger'
                      aria-pressed={active}
                      onClick={() => filter.onClick?.(active)}
                      className='group/omni-table-filter focus-visible:ring-primary-200 inline-flex rounded-[72px] outline-none focus-visible:ring-2 focus-visible:ring-offset-2'
                    >
                      <Chip
                        variant={active ? 'filled' : 'outlined'}
                        size='sm'
                        className={cn(
                          'group-data-[popup-open]/omni-table-filter:[&_[data-slot=chip-label]]:font-semibold',
                          active
                            ? 'group-data-[popup-open]/omni-table-filter:bg-primary'
                            : 'group-data-[popup-open]/omni-table-filter:border-surface-300 group-data-[popup-open]/omni-table-filter:bg-surface-200'
                        )}
                        icon={filter.icon}
                        iconPosition={filter.icon ? 'left' : undefined}
                        trailingIcon={
                          canClear ? (
                            <span className='size-(--text-control-sm--line-height) block' />
                          ) : undefined
                        }
                      >
                        {filter.label}
                        {!!filter.selectedCount && ` (${filter.selectedCount})`}
                      </Chip>
                    </button>
                  )
                  return (
                    <span key={filter.id} className='relative inline-flex'>
                      {filter.renderTrigger
                        ? filter.renderTrigger(trigger)
                        : trigger}
                      {canClear && (
                        <button
                          type='button'
                          aria-label={`Clear ${filter.label} filter`}
                          className={cn(
                            'focus-visible:ring-primary-200 absolute inset-y-0 right-3 flex w-[26px] items-center justify-center rounded-full outline-none hover:bg-white/10 focus-visible:ring-2',
                            active ? 'text-white' : 'text-surface-title-50'
                          )}
                          onClick={event => {
                            event.preventDefault()
                            event.stopPropagation()
                            const opener =
                              event.currentTarget.parentElement?.querySelector<HTMLButtonElement>(
                                '[data-slot="omni-table-filter-trigger"]'
                              )
                            filter.onClear?.()
                            opener?.focus({ preventScroll: true })
                          }}
                        >
                          <OmniTableIcon
                            name='close'
                            className='size-(--text-control-sm--line-height) text-(length:--text-control-sm--line-height)/none'
                          />
                        </button>
                      )}
                    </span>
                  )
                })}
              </div>
              {appliedQuickFilterCount > 1 && onClearFilters && (
                <TextLink
                  href='#'
                  role='button'
                  variant='caption-lg'
                  className='text-control-sm ml-auto whitespace-nowrap'
                  onClick={event => {
                    event.preventDefault()
                    onClearFilters()
                  }}
                  onKeyDown={event => {
                    if (event.key === ' ') {
                      event.preventDefault()
                      onClearFilters()
                    }
                  }}
                >
                  Clear all filters
                </TextLink>
              )}
            </div>
          )}

          <div
            ref={bodyRef}
            data-slot='omni-table-body'
            className='flex min-h-0 min-w-0 flex-1 flex-col gap-2'
          >
            {showFeedback ? (
              <div
                role={error ? 'alert' : 'status'}
                tabIndex={selectionAreaHeight > 0 ? 0 : undefined}
                className={cn(
                  'h-full',
                  selectionAreaHeight > 0 && 'min-h-0 overflow-y-auto'
                )}
              >
                {/* Centers the content, and sizes to it when it is taller. */}
                <div
                  ref={emptyContentRef}
                  data-slot='omni-table-empty-content'
                  className='flex min-h-full flex-col justify-center'
                >
                  {error ? (
                    typeof error === 'string' || error === true ? (
                      <OmniTableEmptyState
                        illustration={
                          // eslint-disable-next-line @next/next/no-img-element -- Acqua embeds this SVG and does not depend on Next.js.
                          <img
                            src={loadErrorIllustration}
                            alt=''
                            width={422}
                            height={404}
                            className='h-auto w-[240px] max-w-full'
                          />
                        }
                        title={
                          typeof error === 'string'
                            ? error
                            : 'Unable to load records.'
                        }
                        description="We couldn't load your data. Please try again."
                      />
                    ) : (
                      error
                    )
                  ) : (
                    emptyMessage
                  )}
                </div>
              </div>
            ) : (
              <>
                {loading && (
                  <Text as='span' role='status' className='sr-only'>
                    Loading…
                  </Text>
                )}
                <TableScrollArea
                  height={scrollAreaHeight}
                  fitContent={fitContent}
                  label={tableLabel}
                  contentKey={viewKey}
                  className='rounded-[8px]'
                >
                  {(
                    hasHorizontalScroll,
                    measure,
                    hasScrolledHorizontally,
                    viewportWidth
                  ) => {
                    const pinFirstColumn =
                      hasScrolledHorizontally &&
                      viewportWidth >= minimumPinnedViewportWidth
                    const fixedTableWidth = getTableWidth()
                    const tableWidth = Math.max(
                      fixedTableWidth,
                      minTableWidth,
                      viewportWidth
                    )
                    const spacerWidth = tableWidth - fixedTableWidth
                    return (
                      <TableTransition
                        viewKey={viewKey}
                        tabIndex={activeTabIndex}
                        enterDirection={enterDirection}
                        page={pagination.page}
                        loading={loading}
                        animated={shouldAnimate}
                        minWidth={Math.max(minTableWidth, getTableWidth())}
                        onContentChange={measure}
                      >
                        <table
                          aria-label={tableLabel}
                          aria-busy={loading || undefined}
                          aria-colcount={
                            spacerWidth > 0
                              ? columns.length + (hasLeadingColumn ? 1 : 0)
                              : undefined
                          }
                          className='table-fixed border-separate border-spacing-0'
                          style={{
                            width: tableWidth,
                            minWidth: fixedTableWidth
                          }}
                        >
                          <thead className='bg-branding-white-50 text-surface-body text-body-lg sticky top-0 z-20 h-10 font-semibold'>
                            <tr className='h-10'>
                              {hasLeadingColumn && (
                                <th
                                  scope='col'
                                  aria-colindex={
                                    spacerWidth > 0 ? 1 : undefined
                                  }
                                  style={columnStyle({
                                    width: SELECTION_COLUMN_WIDTH
                                  })}
                                  data-pinned={
                                    pinFirstColumn ? 'left' : undefined
                                  }
                                  className={cn(
                                    'relative h-10 w-12 p-0 text-left',
                                    pinFirstColumn &&
                                      'bg-branding-white-50 sticky left-0 z-20'
                                  )}
                                >
                                  <div className='absolute inset-y-0 left-4 flex items-center'>
                                    {selection && (
                                      <Checkbox
                                        animated={animated}
                                        appearance='table'
                                        size='md'
                                        aria-label='Select all rows on this page'
                                        checked={allOnPage}
                                        indeterminate={
                                          selectedOnPage.length > 0 &&
                                          !allOnPage
                                        }
                                        disabled={
                                          loading ||
                                          !!error ||
                                          selectablePageIds.length === 0
                                        }
                                        onCheckedChange={checked =>
                                          togglePage(checked === true)
                                        }
                                      />
                                    )}
                                  </div>
                                </th>
                              )}
                              {columns.map((column, columnIndex) => (
                                <Fragment key={column.id}>
                                  {column === firstPinnedColumn &&
                                    spacerWidth > 0 && (
                                      <th
                                        aria-hidden='true'
                                        data-slot='omni-table-actions-spacer'
                                        className='p-0'
                                        style={{ width: spacerWidth }}
                                      />
                                    )}
                                  <motion.th
                                    key={column.id}
                                    layout={
                                      shouldAnimate &&
                                      !column.pinned &&
                                      !(
                                        pinFirstColumn &&
                                        column === firstDataColumn
                                      )
                                        ? 'position'
                                        : false
                                    }
                                    layoutDependency={columnOrder}
                                    transition={{
                                      layout: {
                                        type: 'spring',
                                        stiffness: 500,
                                        damping: 35,
                                        mass: 0.7
                                      }
                                    }}
                                    scope='col'
                                    aria-colindex={
                                      spacerWidth > 0
                                        ? columnIndex +
                                          1 +
                                          (hasLeadingColumn ? 1 : 0)
                                        : undefined
                                    }
                                    aria-sort={
                                      sort?.columnId === column.id
                                        ? sort.direction === 'asc'
                                          ? 'ascending'
                                          : 'descending'
                                        : undefined
                                    }
                                    data-pinned={
                                      pinFirstColumn &&
                                      column === firstDataColumn
                                        ? 'left'
                                        : column.pinned
                                    }
                                    style={getColumnStyle(
                                      column,
                                      pinFirstColumn
                                    )}
                                    className={cn(
                                      'h-10 whitespace-nowrap px-4 font-semibold',
                                      column.pinned === 'right' &&
                                        'bg-branding-white-50 sticky right-0 z-30',
                                      column.pinned === 'right' &&
                                        hasHorizontalScroll &&
                                        'shadow-[-8px_0_12px_rgba(42,46,81,0.04)]',
                                      column.className,
                                      columnAlign(column),
                                      pinFirstColumn &&
                                        column === firstDataColumn &&
                                        'bg-branding-white-50 sticky z-20 shadow-[8px_0_12px_rgba(42,46,81,0.04)]',
                                      column.pinned === 'right' && 'px-4'
                                    )}
                                  >
                                    {column.sortable && onSortChange ? (
                                      <button
                                        type='button'
                                        aria-label={`Sort by ${
                                          typeof column.header === 'string'
                                            ? column.header
                                            : column.id
                                        }`}
                                        onClick={() => {
                                          if (
                                            column.sortCycle === 'desc-asc' ||
                                            (!column.sortCycle &&
                                              column.sortIcon === 'alpha')
                                          ) {
                                            onSortChange({
                                              columnId: column.id,
                                              direction:
                                                sort?.columnId === column.id &&
                                                sort.direction === 'desc'
                                                  ? 'asc'
                                                  : 'desc'
                                            })
                                          } else if (
                                            sort?.columnId === column.id &&
                                            sort.direction === 'desc'
                                          ) {
                                            onSortChange(undefined)
                                          } else {
                                            onSortChange({
                                              columnId: column.id,
                                              direction:
                                                sort?.columnId === column.id
                                                  ? 'desc'
                                                  : 'asc'
                                            })
                                          }
                                        }}
                                        className='focus-visible:ring-primary-200 inline-flex items-center gap-1 rounded outline-none focus-visible:ring-2'
                                      >
                                        <Text
                                          as='span'
                                          variant='body-lg'
                                          weight='semibold'
                                        >
                                          {column.header}
                                        </Text>
                                        {column.sortIcon === 'alpha' &&
                                        sort?.columnId === column.id &&
                                        sort.direction === 'desc' ? (
                                          <DescendingTextSortIcon />
                                        ) : (
                                          <OmniTableIcon
                                            name={
                                              column.sortIcon === 'alpha'
                                                ? 'sort_by_alpha'
                                                : sort?.columnId !== column.id
                                                ? 'swap_vert'
                                                : sort.direction === 'asc'
                                                ? 'vertical_align_bottom'
                                                : 'vertical_align_top'
                                            }
                                            size='inherit'
                                          />
                                        )}
                                      </button>
                                    ) : (
                                      <Text
                                        as='span'
                                        variant='body-lg'
                                        weight='semibold'
                                      >
                                        {column.header}
                                      </Text>
                                    )}
                                  </motion.th>
                                </Fragment>
                              ))}
                              {!firstPinnedColumn && spacerWidth > 0 && (
                                <th
                                  aria-hidden='true'
                                  data-slot='omni-table-trailing-spacer'
                                  className='p-0'
                                  style={{ width: spacerWidth }}
                                />
                              )}
                            </tr>
                          </thead>
                          <tbody className='text-surface-title-50 text-body-lg'>
                            {loading
                              ? Array.from(
                                  { length: MIN_VISIBLE_ROWS },
                                  (_, index) => (
                                    <tr
                                      key={`loading-${index}`}
                                      aria-hidden='true'
                                      className='h-[58px]'
                                    >
                                      {hasLeadingColumn && (
                                        <td
                                          className='border-surface-200 border-t p-0'
                                          style={columnStyle({
                                            width: SELECTION_COLUMN_WIDTH
                                          })}
                                        >
                                          <Skeleton
                                            variant='square'
                                            className='ml-4 size-6'
                                          />
                                        </td>
                                      )}
                                      {columns.map(column => (
                                        <Fragment key={column.id}>
                                          {column === firstPinnedColumn &&
                                            spacerWidth > 0 && (
                                              <td
                                                aria-hidden='true'
                                                className='border-surface-200 border-t p-0'
                                                style={{
                                                  width: spacerWidth
                                                }}
                                              />
                                            )}
                                          <td
                                            style={getColumnStyle(
                                              column,
                                              pinFirstColumn
                                            )}
                                            className={cn(
                                              'border-surface-200 h-[58px] border-t py-2 align-middle',
                                              column.pinned === 'right' &&
                                                'bg-surface sticky right-0 z-10',
                                              pinFirstColumn &&
                                                column === firstDataColumn &&
                                                'bg-surface sticky z-[5]'
                                            )}
                                          >
                                            <Skeleton
                                              className={cn(
                                                'h-4 w-3/4',
                                                columnAlign(column) ===
                                                  'text-right' && 'ml-auto'
                                              )}
                                            />
                                          </td>
                                        </Fragment>
                                      ))}
                                      {!firstPinnedColumn &&
                                        spacerWidth > 0 && (
                                          <td
                                            aria-hidden='true'
                                            className='border-surface-200 border-t p-0'
                                            style={{
                                              width: spacerWidth
                                            }}
                                          />
                                        )}
                                    </tr>
                                  )
                                )
                              : rows.map(row => {
                                  const id = getRowId(row)
                                  const rowProps = getRowProps?.(row)
                                  const expanded = isRowExpanded?.(row) ?? false
                                  return (
                                    <Fragment key={id}>
                                      <tr
                                        data-selected={
                                          selection?.selectedIds.includes(id) ||
                                          undefined
                                        }
                                        {...rowProps}
                                        className={cn(
                                          'group/omni-table-row data-selected:bg-table-row-hover h-[58px]',
                                          !expanded &&
                                            'group/omni-table-hover hover:bg-table-row-hover',
                                          rowProps?.className
                                        )}
                                      >
                                        {hasLeadingColumn && (
                                          <td
                                            aria-colindex={
                                              spacerWidth > 0 ? 1 : undefined
                                            }
                                            style={columnStyle({
                                              width: SELECTION_COLUMN_WIDTH
                                            })}
                                            data-pinned={
                                              pinFirstColumn
                                                ? 'left'
                                                : undefined
                                            }
                                            className={cn(
                                              'group-hover/omni-table-hover:bg-table-row-hover group-data-[selected]/omni-table-row:bg-table-row-hover border-surface-200 relative border-t p-0',
                                              pinFirstColumn &&
                                                'bg-surface sticky left-0 z-[5]'
                                            )}
                                          >
                                            <div className='absolute inset-y-0 left-4 flex items-center'>
                                              {selection ? (
                                                <Checkbox
                                                  animated={animated}
                                                  appearance='table'
                                                  size='md'
                                                  aria-label={`Select row ${id}`}
                                                  checked={selection.selectedIds.includes(
                                                    id
                                                  )}
                                                  disabled={!isSelectable(id)}
                                                  onCheckedChange={checked =>
                                                    toggleRow(
                                                      id,
                                                      checked === true
                                                    )
                                                  }
                                                />
                                              ) : (
                                                renderRowControl?.(row)
                                              )}
                                            </div>
                                          </td>
                                        )}
                                        {columns.map((column, columnIndex) => (
                                          <Fragment key={column.id}>
                                            {column === firstPinnedColumn &&
                                              spacerWidth > 0 && (
                                                <td
                                                  aria-hidden='true'
                                                  data-slot='omni-table-actions-spacer'
                                                  className='group-hover/omni-table-hover:bg-table-row-hover group-data-[selected]/omni-table-row:bg-table-row-hover border-surface-200 border-t p-0'
                                                  style={{
                                                    width: spacerWidth
                                                  }}
                                                />
                                              )}
                                            <motion.td
                                              key={column.id}
                                              layout={
                                                shouldAnimate &&
                                                !column.pinned &&
                                                !(
                                                  pinFirstColumn &&
                                                  column === firstDataColumn
                                                )
                                                  ? 'position'
                                                  : false
                                              }
                                              layoutDependency={columnOrder}
                                              transition={{
                                                layout: {
                                                  type: 'spring',
                                                  stiffness: 500,
                                                  damping: 35,
                                                  mass: 0.7
                                                }
                                              }}
                                              data-pinned={
                                                pinFirstColumn &&
                                                column === firstDataColumn
                                                  ? 'left'
                                                  : column.pinned
                                              }
                                              aria-colindex={
                                                spacerWidth > 0
                                                  ? columnIndex +
                                                    1 +
                                                    (hasLeadingColumn ? 1 : 0)
                                                  : undefined
                                              }
                                              style={getColumnStyle(
                                                column,
                                                pinFirstColumn
                                              )}
                                              className={cn(
                                                'group-hover/omni-table-hover:bg-table-row-hover group-data-[selected]/omni-table-row:bg-table-row-hover border-surface-200 h-[58px] border-t px-4 align-middle',
                                                column.contentType === 'name'
                                                  ? 'py-4'
                                                  : 'py-2',
                                                column.pinned === 'right' &&
                                                  'bg-surface sticky right-0 z-10',
                                                column.pinned === 'right' &&
                                                  hasHorizontalScroll &&
                                                  'shadow-[-8px_0_12px_rgba(42,46,81,0.04)]',
                                                column.className,
                                                columnAlign(column),
                                                pinFirstColumn &&
                                                  column === firstDataColumn &&
                                                  'bg-surface sticky z-[5] shadow-[8px_0_12px_rgba(42,46,81,0.04)]',
                                                column.pinned === 'right' &&
                                                  'px-4'
                                              )}
                                            >
                                              <PinnedCellContent
                                                columnId={column.id}
                                                rowId={`row:${id}`}
                                                onWidthChange={
                                                  measurePinnedWidth
                                                }
                                                measureWidth={
                                                  column.pinned === 'right'
                                                }
                                              >
                                                <TableCellContent
                                                  content={column.cell(
                                                    row,
                                                    cellContext
                                                  )}
                                                  context={cellContext}
                                                />
                                              </PinnedCellContent>
                                            </motion.td>
                                          </Fragment>
                                        ))}
                                        {!firstPinnedColumn &&
                                          spacerWidth > 0 && (
                                            <td
                                              aria-hidden='true'
                                              data-slot='omni-table-trailing-spacer'
                                              className='group-hover/omni-table-hover:bg-table-row-hover group-data-[selected]/omni-table-row:bg-table-row-hover border-surface-200 border-t p-0'
                                              style={{
                                                width: spacerWidth
                                              }}
                                            />
                                          )}
                                      </tr>
                                      {getSubRows?.(row)?.map(subRow => {
                                        const subRowProps =
                                          getRowProps?.(subRow)
                                        return (
                                          <tr
                                            key={getRowId(subRow)}
                                            data-sub-row=''
                                            aria-hidden={!expanded || undefined}
                                            {...subRowProps}
                                            className={cn(
                                              'group/omni-table-row bg-surface',
                                              subRowProps?.className
                                            )}
                                          >
                                            {hasLeadingColumn && (
                                              <td
                                                aria-colindex={
                                                  spacerWidth > 0
                                                    ? 1
                                                    : undefined
                                                }
                                                className={cn(
                                                  'border-surface-200 p-0',
                                                  expanded && 'border-t',
                                                  pinFirstColumn &&
                                                    'sticky left-0 z-[5] bg-inherit'
                                                )}
                                              />
                                            )}
                                            {columns.map(
                                              (column, columnIndex) => (
                                                <Fragment key={column.id}>
                                                  {column ===
                                                    firstPinnedColumn &&
                                                    spacerWidth > 0 && (
                                                      <td
                                                        aria-hidden='true'
                                                        data-slot='omni-table-actions-spacer'
                                                        className={cn(
                                                          'group-hover/omni-table-hover:bg-table-row-hover p-0',
                                                          expanded &&
                                                            'border-surface-200 border-t'
                                                        )}
                                                        style={{
                                                          width: spacerWidth
                                                        }}
                                                      />
                                                    )}
                                                  <td
                                                    key={column.id}
                                                    data-pinned={
                                                      pinFirstColumn &&
                                                      column === firstDataColumn
                                                        ? 'left'
                                                        : column.pinned
                                                    }
                                                    aria-colindex={
                                                      spacerWidth > 0
                                                        ? columnIndex +
                                                          1 +
                                                          (hasLeadingColumn
                                                            ? 1
                                                            : 0)
                                                        : undefined
                                                    }
                                                    style={getColumnStyle(
                                                      column,
                                                      pinFirstColumn
                                                    )}
                                                    className={cn(
                                                      'border-surface-200 p-0 align-middle',
                                                      expanded && 'border-t',
                                                      column.pinned ===
                                                        'right' &&
                                                        'sticky right-0 z-10 bg-inherit',
                                                      column.className,
                                                      columnAlign(column),
                                                      pinFirstColumn &&
                                                        column ===
                                                          firstDataColumn &&
                                                        'sticky z-[5] bg-inherit'
                                                    )}
                                                  >
                                                    {/* Padding collapses with the content. The cell's divider
                                                        is present only while expanded, across the full gutter. */}
                                                    <div
                                                      className={cn(
                                                        'grid',
                                                        shouldAnimate &&
                                                          'transition-[grid-template-rows] duration-300 ease-out',
                                                        expanded
                                                          ? 'grid-rows-[1fr]'
                                                          : 'grid-rows-[0fr]'
                                                      )}
                                                    >
                                                      <div className='min-h-0 overflow-hidden'>
                                                        <div
                                                          className={cn(
                                                            'flex min-h-[58px] flex-col justify-center',
                                                            column.contentType ===
                                                              'name'
                                                              ? 'py-4'
                                                              : 'py-2'
                                                          )}
                                                        >
                                                          <TableCellContent
                                                            content={column.cell(
                                                              subRow,
                                                              cellContext
                                                            )}
                                                            context={
                                                              cellContext
                                                            }
                                                          />
                                                        </div>
                                                      </div>
                                                    </div>
                                                  </td>
                                                </Fragment>
                                              )
                                            )}
                                            {!firstPinnedColumn &&
                                              spacerWidth > 0 && (
                                                <td
                                                  aria-hidden='true'
                                                  data-slot='omni-table-trailing-spacer'
                                                  className={cn(
                                                    'group-hover/omni-table-hover:bg-table-row-hover p-0',
                                                    expanded &&
                                                      'border-surface-200 border-t'
                                                  )}
                                                  style={{
                                                    width: spacerWidth
                                                  }}
                                                />
                                              )}
                                          </tr>
                                        )
                                      })}
                                    </Fragment>
                                  )
                                })}
                          </tbody>
                        </table>
                      </TableTransition>
                    )
                  }}
                </TableScrollArea>
                <div
                  ref={footerRef}
                  data-slot='omni-table-footer'
                  className='shrink-0'
                >
                  <TableFooter pagination={pagination} animated={animated} />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <SelectionBar
        selection={selection}
        count={selectedCount}
        animated={animated}
        containerRef={selectionAreaRef}
      />
    </div>
  )
}

const OmniTableWithRef = forwardRef(OmniTableInner)
OmniTableWithRef.displayName = 'OmniTable'

export const OmniTable = OmniTableWithRef as <Row>(
  props: OmniTableProps<Row> & RefAttributes<HTMLDivElement>
) => React.ReactElement
